import { ref, watch, onMounted, onUnmounted, type Ref } from "vue";
import type {
  PaymentMethod,
  SelectablePaymentOptions,
  PayabliConfig,
} from "@/types";
import { usePayabliConfig } from "./usePayabliConfig";
import { usePayabliComponentLifecycle } from "./usePayabliComponentLifecycle";
import {
  PAYMENT_OPTIONS,
  PAYABLI_EXEC_METHOD,
  PAYABLI_EXEC_REINIT,
} from "../constants";

export interface UsePayabliEmbeddedComponent {
  apiToken: Ref<string>;
  entryPointName: Ref<string>;
  createTemporaryPaymentMethod: Ref<boolean>;
  showingCreditCardPaymentOption: Ref<boolean>;
  selectedPaymentMethod: Ref<PaymentMethod>;
  creditCardFormValid: Ref<boolean>;
  payabliResponse: Ref<object>;
  onPay: () => void;
  onSwitchPaymentOption: (selectedPaymentOption: PaymentMethod) => void;
  isInitialized: Ref<boolean>;
  loadScript: () => Promise<void>;
  reInitializePayabliComponent: () => void;
  selectedPaymentOption: Ref<SelectablePaymentOptions>;
}

/**
 * Composable for managing Payabli embedded payment component
 * @param payabliApiToken Payabli API token
 * @param payabliEntryPointName Payabli entry point name
 * @param makePayment Function to process payment after Payabli returns a reference
 * @param customerNumber Customer identifier
 * @param initialPaymentMethod Initial payment method (defaults to 'card')
 * @returns Payabli component state and methods
 */
export const usePayabliEmbeddedComponent = (
  payabliApiToken: string,
  payabliEntryPointName: string,
  makePayment: (
    referenceType: PaymentMethod,
    referenceId: string,
    paymentOption: SelectablePaymentOptions
  ) => Promise<boolean>,
  customerNumber: string,
  initialPaymentMethod: PaymentMethod = "card"
): UsePayabliEmbeddedComponent => {
  const apiToken: Ref<string> = ref(payabliApiToken);
  const entryPointName: Ref<string> = ref(payabliEntryPointName);
  const createTemporaryPaymentMethod: Ref<boolean> = ref(false);
  const payabliResponse: Ref<object> = ref({});
  const creditCardFormValid: Ref<boolean> = ref(false);
  const selectedPaymentMethod: Ref<PaymentMethod> = ref(initialPaymentMethod);
  const selectedPaymentOption: Ref<SelectablePaymentOptions> = ref(
    PAYMENT_OPTIONS.ONE_TIME
  );
  const timeout: Ref<number | undefined> = ref(undefined);

  const {
    isInitialized,
    loadScript,
    initialize,
    reinitialize,
    getComponent,
    setInitialized,
  } = usePayabliComponentLifecycle();

  const handleSuccess = async (
    referenceId: string,
    referenceType: PaymentMethod,
    paymentOption: SelectablePaymentOptions
  ) => {
    const success = await makePayment(
      referenceType,
      referenceId,
      paymentOption
    );

    if (!success) {
      const component = getComponent();
      if (component) {
        component.payabliExec(PAYABLI_EXEC_REINIT);
      }
    }
  };

  const handleReady = (isValid: boolean) => {
    // This checks that all of the fields are populated + valid
    creditCardFormValid.value = isValid;
    // Use the documented callback to set initialization status
    setInitialized(true);
  };

  const handleError = (errors: any) => {
    console.error("Payabli error:", errors);
  };

  const { createConfig, updateConfig, showingCreditCardPaymentOption } =
    usePayabliConfig({
      apiToken: payabliApiToken,
      entryPointName: payabliEntryPointName,
      customerNumber,
      selectedPaymentMethod,
      createTemporaryPaymentMethod,
      onSuccess: handleSuccess,
      onReady: handleReady,
      onError: handleError,
    });

  const payabliConfig: PayabliConfig = createConfig(selectedPaymentOption);

  initialize(payabliConfig);

  // Fallback timeout to mark as initialized if callback isn't called consistently
  onMounted(() => {
    timeout.value = window.setTimeout(() => {
      if (!isInitialized.value) {
        setInitialized(true);
      }
    }, 5000);
  });

  onUnmounted(() => {
    if (timeout.value) {
      clearTimeout(timeout.value);
    }
  });

  const onPay = () => {
    const component = getComponent();
    if (!component) {
      console.error("Payabli component not initialized");
      return;
    }
    component.payabliExec(PAYABLI_EXEC_METHOD);
  };

  const onSwitchPaymentOption = (selectedPaymentOption: PaymentMethod) => {
    selectedPaymentMethod.value = selectedPaymentOption;

    const newConfig = updateConfig(
      payabliConfig,
      showingCreditCardPaymentOption.value
    );

    const component = getComponent();
    if (component) {
      component.updateConfig(newConfig);
    }
  };

  watch(createTemporaryPaymentMethod, () => {
    payabliConfig.temporaryToken = createTemporaryPaymentMethod.value;

    const newConfig = updateConfig(
      payabliConfig,
      showingCreditCardPaymentOption.value
    );

    const component = getComponent();
    if (component) {
      component.updateConfig(newConfig);
    }
  });

  const reInitializePayabliComponent = () => {
    reinitialize(payabliConfig);
  };

  return {
    apiToken,
    entryPointName,
    createTemporaryPaymentMethod,
    showingCreditCardPaymentOption,
    selectedPaymentMethod,
    payabliResponse,
    creditCardFormValid,
    onPay,
    onSwitchPaymentOption,
    isInitialized,
    loadScript,
    reInitializePayabliComponent,
    selectedPaymentOption,
  };
};
